import React from 'react';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Calculator, Info, AlertCircle, CheckCircle, Zap, Clock } from 'lucide-react';
import NorgesPrisCalculator from '../components/NorgesPrisCalculator';
import { PriceArea } from '../types';
import GoogleAd from '../components/GoogleAd';
import SidebarAd from '../components/SidebarAd';

const NorgesPris: React.FC = () => {
  const [selectedArea, setSelectedArea] = React.useState<PriceArea>('NO1');

  // Track page view for Norgespris page
  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', {
        page_title: 'Norgespris Calculator',
        page_location: window.location.href,
        event_category: 'Page View',
        event_label: 'Norgespris Page Visited'
      });
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>{`Norgespris Kalkulator ${new Date().getFullYear()} | Test om Norgespris lønner seg for deg`}</title>
        <meta name="description" content="Norgespris kalkulator: Test om Norgespris på 40 øre/kWh (50 øre med mva.) lønner seg for deg ✓ Sammenlign med strømstøtte ✓ Beregn besparelse ✓ Bindingstid ut 2026 ✓ 5000 kWh forbruksgrense ✓ Gratis kalkulator fra Strømkalkulator.no" />
        <meta name="keywords" content={`norgespris kalkulator, norgespris ${new Date().getFullYear()}, norgespris beregning, norgespris lønner seg, norgespris vs strømstøtte, norgespris fastpris 50 øre, norgespris forbruksgrense, norgespris elhub, strømstøtte kalkulator, sammenlign norgespris, norgespris bindingstid 2026, kraftpris kalkulator`} />
        <meta name="news_keywords" content={`norgespris kalkulator, norgespris ${new Date().getFullYear()}, fastpris strøm, strømstøtte beregning, kraftpris kalkulator`} />
        
        {/* Open Graph meta tags */}
        <meta property="og:title" content="Norgespris 2027: Alle må bestille på nytt | Test om det lønner seg" />
        <meta property="og:description" content="Norgespris er 40 øre/kWh uten mva. ut 2026, og 45 øre er foreslått for 2027. Alle må bestille på nytt for 2027. Test om det lønner seg for deg." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://stromkalkulator.no/norgespris" />
        <meta property="og:site_name" content="Strømkalkulator.no" />
        <meta property="og:locale" content="nb_NO" />
        
        {/* Twitter Card data */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Norgespris 2027: Alle må bestille på nytt | Test om det lønner seg" />
        <meta name="twitter:description" content="Norgespris er 40 øre/kWh uten mva. ut 2026, og 45 øre er foreslått for 2027. Test om det lønner seg for deg med vår kalkulator." />
        
        {/* Additional SEO meta tags */}
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="bingbot" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <link rel="canonical" href="https://stromkalkulator.no/norgespris" />
        <meta name="author" content="Strømkalkulator.no" />
        <link rel="alternate" hrefLang="no" href="https://stromkalkulator.no/norgespris" />
        <link rel="alternate" hrefLang="x-default" href="https://stromkalkulator.no/norgespris" />

        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Norgespris-kalkulator 2026/2027 - Test om Norgespris lønner seg",
            "description": "Test om Norgespris (40 øre/kWh uten mva. i 2026, 45 øre foreslått for 2027) lønner seg for deg sammenlignet med strømstøtte. Alle må bestille på nytt for 2027.",
            "publisher": {
              "@type": "Organization",
              "name": "Strømkalkulator.no",
              "url": "https://stromkalkulator.no"
            },
            "dateModified": new Date().toISOString(),
            "isPartOf": {
              "@type": "WebSite",
              "url": "https://stromkalkulator.no"
            },
            "mainEntity": {
              "@type": "CalculatorService",
              "name": "Norgespris Kalkulator",
              "description": "Kalkulator for å sammenligne Norgespris med dagens strømstøtteordning",
              "provider": {
                "@type": "Organization",
                "name": "Strømkalkulator.no"
              }
            }
          })}
        </script>

        {/* FAQ structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Hva er Norgespris?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Norgespris er en fastprisordning på strøm som gjelder fra 1. oktober 2025. Den gir forbrukere mulighet til å velge en fast strømpris på 40 øre/kWh uten mva. (50 øre med mva., 40 øre i Nord-Norge) som alternativ til dagens strømstøtteordning."
                }
              },
              {
                "@type": "Question",
                "name": "Hvordan fungerer Norgespris?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Norgespris er en fastprisavtale på 40 øre/kWh (50 øre inkl. moms) som bestilles på elhub.no. Du beholder din strømleverandør, men får fast pris på strømmen. Bindingstid ut 2026."
                }
              },
              {
                "@type": "Question",
                "name": "Hvor bestiller jeg Norgespris?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Norgespris bestilles på elhub.no med elektronisk ID (BankID). Ordningen er tilgjengelig for alle private husholdninger og fritidsboliger i Norge."
                }
              },
              {
                "@type": "Question",
                "name": "Hva er forbruksgrensen for Norgespris?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Forbruksgrensen er 5000 kWh per måned for boliger og 1000 kWh for fritidsboliger. Forbruk over denne grensen faktureres til spotpris fra din strømleverandør."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <main className="flex-grow container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto relative">
          {/* Left Sidebar Ad */}
          <aside className="hidden xl:block absolute left-0 top-0 -translate-x-[calc(100%+2rem)]">
            <SidebarAd slot="1519579735" />
          </aside>

          {/* Right Sidebar Ad */}
          <aside className="hidden xl:block absolute right-0 top-0 translate-x-[calc(100%+2rem)]">
            <SidebarAd slot="1519579735" />
          </aside>

          {/* Calculator Section */}
          <div id="calculator" className="mb-12">
            {/* Google Ad */}
            <div className="mb-8 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <GoogleAd
                slot="3068465121"
                format="fluid"
                layout="in-article"
                style={{
                  display: 'block',
                  margin: '0 auto',
                  textAlign: 'center',
                  minHeight: '100px'
                }}
              />
            </div>

            <NorgesPrisCalculator area={selectedArea} onAreaChange={setSelectedArea} />
          </div>

          {/* Google Ad */}
          <div className="mb-8 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <GoogleAd
              slot="3068465121"
              format="fluid"
              layout="in-article"
              style={{
                display: 'block',
                margin: '0 auto',
                textAlign: 'center',
                minHeight: '100px'
              }}
            />
          </div>

          {/* Key Points */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="bg-blue-100 dark:bg-blue-900/30 w-12 h-12 flex items-center justify-center rounded-lg mb-4">
                <Clock className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Bindingstid</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Bindingstid fra du bestiller til 31. desember 2026. Avtalen er knyttet til boligen, ikke person.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="bg-blue-100 dark:bg-blue-900/30 w-12 h-12 flex items-center justify-center rounded-lg mb-4">
                <Zap className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Fast pris</h3>
              <p className="text-gray-600 dark:text-gray-300">
                40 øre/kWh (50 øre inkl. moms). Nettleie, avgifter og påslag kommer i tillegg.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="bg-blue-100 dark:bg-blue-900/30 w-12 h-12 flex items-center justify-center rounded-lg mb-4">
                <AlertCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">Forbruksgrense</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Maksgrense på 5000 kWh per måned for boliger, 1000 kWh for fritidsboliger.
              </p>
            </div>
          </div>

          {/* Detailed Information */}
          <div id="info" className="space-y-8">
            {/* How to order section */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl p-8 shadow-lg">
              <div className="text-center">
                <div className="bg-white/20 backdrop-blur-sm p-3 rounded-full inline-flex mb-4">
                  <CheckCircle className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-3xl font-bold mb-4">Slik bestiller du Norgespris</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                    <div className="text-4xl font-bold mb-2">1</div>
                    <h3 className="font-semibold mb-2">Gå til elhub.no</h3>
                    <p className="text-blue-100 text-sm">Logg inn med BankID på minside.elhub.no</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                    <div className="text-4xl font-bold mb-2">2</div>
                    <h3 className="font-semibold mb-2">Test kalkulatoren</h3>
                    <p className="text-blue-100 text-sm">Se om Norgespris lønner seg for ditt forbruk</p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                    <div className="text-4xl font-bold mb-2">3</div>
                    <h3 className="font-semibold mb-2">Bestill avtalen</h3>
                    <p className="text-blue-100 text-sm">Bindingstid ut 2026. Alle må bestille på nytt for 2027.</p>
                  </div>
                </div>
                <a
                  href="https://minside.elhub.no"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 rounded-xl hover:bg-gray-50 transition-colors font-bold text-lg shadow-lg"
                >
                  <Zap className="w-6 h-6 mr-2" />
                  Bestill på elhub.no
                </a>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl p-8 shadow-sm border border-gray-200 dark:border-gray-700">
              <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Norgespris i 2027: Du må bestille på nytt</h2>
              <div className="prose prose-blue dark:prose-invert max-w-none">
                <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-lg border border-green-100 dark:border-green-800 mb-8">
                  <p className="text-lg mb-0">
                    Norgespris fornyes ikke automatisk. Alle som vil ha Norgespris i 2027, må bestille på nytt på elhub.no. Bestillingen åpner etter at Stortinget har vedtatt statsbudsjettet i desember, og bestiller du i løpet av de første 14 dagene i januar, gjelder prisen fra 1. januar 2027. Regjeringen har foreslått 45 øre/kWh uten mva. for 2027. Du velger mellom to ordninger:
                  </p>
                  <ul className="mt-4 space-y-2">
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      Dagens strømstøtteordning
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      <strong>Norgespris:</strong> Fastpris på 40 øre/kWh uten mva. (50 øre med mva.) ut 2026
                    </li>
                  </ul>
                </div>

                <h3>Slik fungerer det i praksis</h3>
                <div className="bg-gray-50 dark:bg-gray-800/50 p-6 rounded-lg border border-gray-200 dark:border-gray-700 mb-8">
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <span className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400 mr-3 mt-0.5">0</span>
                      <span><strong>Bestill på elhub.no</strong> med BankID. For 2026 kan du bestille ut året, for 2027 åpner bestillingen etter budsjettvedtaket.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400 mr-3 mt-0.5">1</span>
                      <span><strong>Beholder din strømleverandør</strong> - Norgespris er et tillegg til din eksisterende avtale.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400 mr-3 mt-0.5">2</span>
                      <span><strong>Fast pris ut 2026</strong> - Bindingstid til 31. desember 2026, uansett spotpris.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="w-6 h-6 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400 mr-3 mt-0.5">3</span>
                      <span><strong>Avtalen følger boligen</strong> - Ved salg overtar ny eier avtalen.</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-6 rounded-lg border border-yellow-200 dark:border-yellow-800 mb-8">
                  <div className="flex items-start">
                    <AlertCircle className="h-6 w-6 text-yellow-600 dark:text-yellow-400 mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-2">Viktig å vite før du bestiller</h4>
                      <ul className="space-y-2 text-yellow-700 dark:text-yellow-300 text-sm">
                        <li>• <strong>Bindingstid ut 2026:</strong> Du kan ikke si opp avtalen før 31. desember 2026 (14 dagers angrerett)</li>
                        <li>• <strong>Ny bestilling for 2027:</strong> Avtalen fornyes ikke automatisk</li>
                        <li>• <strong>Avtalen følger boligen:</strong> Ved salg overtar kjøper avtalen</li>
                        <li>• <strong>Har lønt seg i Sør- og Midt-Norge:</strong> Prisen etter strømstøtte var i snitt godt over 40 øre det siste året</li>
                        <li>• <strong>Usikkert i Nord-Norge:</strong> Spotprisen var under 40 øre store deler av året</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <h3>Viktige punkter</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                  <div className="bg-gray-50 dark:bg-gray-800/50 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h4 className="font-medium mb-4">Priser og vilkår</h4>
                    <ul className="space-y-3">
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                        <strong>Fast pris på 40 øre/kWh uten mva. (50 øre med mva.)</strong> - bestill på elhub.no
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                        Gjelder private boliger og fritidsboliger (ikke næringsliv)
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                        <strong>Frivillig ordning</strong> - du velger selv
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-gray-400 rounded-full mr-2"></span>
                        <strong>Må bestilles på nytt for 2027</strong> på elhub.no
                      </li>
                    </ul>
                  </div>
                  
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg border border-blue-100 dark:border-blue-800">
                    <h4 className="font-medium mb-4">Forbruksgrense</h4>
                    <div className="space-y-4">
                      <p>
                        <strong>Maksgrenser per måned:</strong>
                      </p>
                      <ul className="space-y-2">
                        <li className="flex items-center">
                          <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                          <span><strong>Boliger:</strong> 5000 kWh per måned</span>
                        </li>
                        <li className="flex items-center">
                          <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                          <span><strong>Fritidsboliger:</strong> 1000 kWh per måned</span>
                        </li>
                      </ul>
                      <p className="text-sm mt-3 bg-blue-100 dark:bg-blue-800/30 p-3 rounded">
                        <strong>Overforbruk:</strong> Forbruk over grensen faktureres til spotpris fra din strømleverandør.
                      </p>
                    </div>
                  </div>
                </div>

                <h3>Hvem anbefales Norgespris for?</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                    <h4 className="font-semibold text-green-800 dark:text-green-200 mb-2">✅ Anbefales</h4>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      <strong>Sør- og Vestlandet (NO1, NO2, NO5):</strong> Med 16 000 kWh i året ville Norgespris vært rundt 7 000 kr billigere enn strømstøtte det siste året.
                    </p>
                  </div>
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
                    <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-2">✅ Lønte seg i fjor</h4>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      <strong>Midt-Norge (NO3):</strong> Rundt 5 000 kr billigere det siste året, men prisene svinger mer enn i sør.
                    </p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                    <h4 className="font-semibold text-red-800 dark:text-red-200 mb-2">⚠️ Usikkert</h4>
                    <p className="text-sm text-red-700 dark:text-red-300">
                      <strong>Nord-Norge (NO4):</strong> Strømstøtte var rundt 1 200 kr billigere det siste året, men vinterprisene var høye.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-8 shadow-sm border border-green-100 dark:border-green-800">
              <div className="flex items-start space-x-4">
                <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-lg flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">Fordeler med Norgespris</h3>
                  <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 dark:bg-green-400 rounded-full mr-2"></span>
                      <strong>Forutsigbare strømregninger</strong> - vet nøyaktig hva du betaler
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 dark:bg-green-400 rounded-full mr-2"></span>
                      <strong>Beskyttelse mot prishopp</strong> - fast 50 øre/kWh med mva. (40 øre i Nord-Norge)
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 dark:bg-green-400 rounded-full mr-2"></span>
                      <strong>Enkel bestilling</strong> - bestilles direkte på elhub.no
                    </li>
                    <li className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 dark:bg-green-400 rounded-full mr-2"></span>
                      <strong>Høy forbruksgrense</strong> - 5000 kWh/måned for boliger
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Call to action */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl p-8 text-center shadow-lg">
              <h2 className="text-2xl font-bold mb-4">Klar for å bestille Norgespris?</h2>
              <p className="mb-6 text-blue-100">
                Test først om det lønner seg med vår kalkulator, deretter bestill på elhub.no
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => document.getElementById('calculator')?.scrollIntoView({ behavior: 'smooth' })}
                  className="inline-flex items-center justify-center px-6 py-3 bg-white/20 backdrop-blur-sm text-white rounded-lg hover:bg-white/30 transition-colors font-medium"
                >
                  <Calculator className="w-5 h-5 mr-2" />
                  Test kalkulatoren
                </button>
                <a
                  href="https://minside.elhub.no"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-blue-600 rounded-lg hover:bg-gray-50 transition-colors font-bold"
                >
                  <Zap className="w-5 h-5 mr-2" />
                  Bestill på elhub.no
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default NorgesPris;